import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';
import { toast } from 'react-toastify';
import CodingRenderer from './components/CodingRenderer';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const getStudentAuthHeaders = () => {
  const token = localStorage.getItem('student_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const findContentInCourse = (chapters, contentId) => {
  for (const chapter of chapters || []) {
    const items = (chapter.items || []).filter((it) => it.status === 'published');
    const match = items.find((it) => String(it.dbId || it.id) === String(contentId));
    if (match) {
      return { chapter, content: match };
    }
  }
  return { chapter: null, content: null };
};

export default function CodingChallengeScreen() {
  const { courseId, contentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [courseName, setCourseName] = useState('');
  const [chapterTitle, setChapterTitle] = useState(location.state?.chapterTitle || '');
  const [contentTitle, setContentTitle] = useState(location.state?.contentTitle || '');
  const [contentData, setContentData] = useState(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/lms/courses/${courseId}/builder`,
          {
            headers: getStudentAuthHeaders(),
            params: { publishedOnly: true },
          },
        );
        if (!active) return;

        if (!response.data?.success) {
          toast.error('Could not load challenge');
          navigate(`/student/courses/${courseId}`, { replace: true });
          return;
        }

        const chapters = (response.data.chapters || []).filter(
          (ch) => ch.status === 'published',
        );
        const { chapter, content } = findContentInCourse(chapters, contentId);

        if (!content) {
          toast.error('Challenge not found');
          navigate(`/student/courses/${courseId}`, { replace: true });
          return;
        }

        setCourseName(response.data.courseName || response.data.courseId || courseId);
        setChapterTitle(chapter?.title || chapterTitle);
        setContentTitle(content.title || contentTitle);
        setContentData(content.contentData || content.content_data || content);
      } catch {
        if (active) {
          toast.error('Failed to load challenge');
          navigate(`/student/courses/${courseId}`, { replace: true });
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [courseId, contentId, navigate, chapterTitle, contentTitle]);

  const resolvedContentId = useMemo(
    () => contentData?.dbId || contentId,
    [contentData, contentId],
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: { xs: '100dvh', sm: '100vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#FFFFFF',
        }}
      >
        <CircularProgress sx={{ color: '#2563EB' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { xs: '100dvh', sm: '100vh' },
        minHeight: { xs: '100dvh', sm: '100vh' },
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FFFFFF',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.5,
          minHeight: 40,
          bgcolor: '#F8F9FA',
          borderBottom: '1px solid #E4E7EB',
          flexShrink: 0,
          pt: 'max(0.5rem, env(safe-area-inset-top))',
        }}
      >
        <IconButton
          size="small"
          onClick={() => navigate(`/student/courses/${courseId}`)}
          sx={{ color: '#374151' }}
          aria-label="Back to course"
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: '#6B7280', display: 'block' }} noWrap>
            {courseName}
            {chapterTitle ? ` · ${chapterTitle}` : ''}
          </Typography>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ color: '#1F2937' }}>
            {contentTitle || 'Coding Challenge'}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          pb: 'env(safe-area-inset-bottom)',
        }}
      >
        <CodingRenderer
          contentId={resolvedContentId}
          contentData={contentData}
          title={contentTitle}
          focusedMode
        />
      </Box>
    </Box>
  );
}
